import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SlaConfiguration {
  id: string;
  tenantId: string;
  jobType: string;
  targetHours: number;
  warningThresholdPercent: number;
  escalationRecipients: string[];
  escalationTimingHours: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SlaBreach {
  id: string;
  jobId: string;
  jobTitle: string;
  jobType: string;
  clientName: string;
  assigneeName: string;
  targetHours: number;
  elapsedHours: number;
  breachedAt: string;
  status: 'warning' | 'breached' | 'escalated';
}

export interface SlaPerformance {
  totalJobs: number;
  jobsMeetingSla: number;
  complianceRate: number;
  averageCompletionHours: number;
  breachCount: number;
  byJobType: SlaPerformanceByType[];
}

export interface SlaPerformanceByType {
  jobType: string;
  totalJobs: number;
  complianceRate: number;
  averageHours: number;
  breachCount: number;
}

export interface CreateSlaConfigPayload {
  jobType: string;
  targetHours: number;
  warningThresholdPercent: number;
  escalationRecipients: string[];
  escalationTimingHours: number;
}

export interface UpdateSlaConfigPayload extends Partial<CreateSlaConfigPayload> {
  isActive?: boolean;
}

export interface SlaBreachesParams {
  status?: 'warning' | 'breached' | 'escalated' | '';
  jobType?: string;
}

export interface SlaPerformanceParams {
  dateFrom?: string;
  dateTo?: string;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const slaKeys = {
  all: ['sla'] as const,
  configurations: () => [...slaKeys.all, 'configurations'] as const,
  breaches: (params?: SlaBreachesParams) => [...slaKeys.all, 'breaches', params] as const,
  performance: (params?: SlaPerformanceParams) => [...slaKeys.all, 'performance', params] as const,
};

// ─── Configuration Hooks ────────────────────────────────────────────────────

/**
 * Fetch all SLA configurations for the current tenant.
 * Requirements: 19.1, 19.2
 */
export function useSlaConfigurations() {
  return useQuery({
    queryKey: slaKeys.configurations(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<SlaConfiguration[]>>('/sla/configurations');
      return response.data.data;
    },
  });
}

/**
 * Create a new SLA configuration.
 * Requirements: 19.2
 */
export function useCreateSlaConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateSlaConfigPayload) => {
      const response = await apiClient.post<ApiResponse<SlaConfiguration>>(
        '/sla/configurations',
        payload,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: slaKeys.configurations() });
    },
  });
}

/**
 * Update an existing SLA configuration.
 * Requirements: 19.2
 */
export function useUpdateSlaConfig(configId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateSlaConfigPayload) => {
      const response = await apiClient.put<ApiResponse<SlaConfiguration>>(
        `/sla/configurations/${configId}`,
        payload,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: slaKeys.configurations() });
    },
  });
}

// ─── Breach Hooks ───────────────────────────────────────────────────────────

/**
 * Fetch active SLA breaches (jobs currently exceeding SLA).
 * Requirements: 19.1
 */
export function useSlaBreaches(params: SlaBreachesParams = {}) {
  return useQuery({
    queryKey: slaKeys.breaches(params),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<SlaBreach[]>>('/sla/breaches', {
        params: {
          status: params.status || undefined,
          jobType: params.jobType || undefined,
        },
      });
      return response.data.data;
    },
    refetchInterval: 60_000, // Refresh every 60s for near real-time breach awareness
  });
}

// ─── Performance Hooks ──────────────────────────────────────────────────────

/**
 * Fetch SLA performance metrics (compliance rate, average completion, breakdown by type).
 * Requirements: 19.1
 */
export function useSlaPerformance(params: SlaPerformanceParams = {}) {
  return useQuery({
    queryKey: slaKeys.performance(params),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<SlaPerformance>>('/sla/performance', {
        params: {
          dateFrom: params.dateFrom || undefined,
          dateTo: params.dateTo || undefined,
        },
      });
      return response.data.data;
    },
  });
}
